<!--
  Agent 工作台入口。
  聊天消息里保留轻量触发器，完整的任务、计划、工具、文件、日志和交付信息放到右侧抽屉。
-->
<template>
  <section class="agent-workbench-entry">
    <!-- 触发按钮 -->
    <button
      type="button"
      class="workbench-trigger"
      :class="{ 'needs-confirmation': hasPendingConfirmation }"
      @click="openDrawer"
    >
      <el-icon class="trigger-icon">
        <Monitor />
      </el-icon>
      <span>{{ triggerText }}</span>
      <span v-if="hasPendingConfirmation" class="confirmation-pill">待确认</span>
      <el-icon class="trigger-arrow">
        <ArrowRight />
      </el-icon>
    </button>

    <Teleport to="body">
      <aside v-show="drawerVisible" class="agent-workbench" role="dialog" aria-modal="false"
        aria-labelledby="agent-workbench-title">
        <!-- 标题 -->
        <header class="workbench-header">
          <div>
            <!-- <p class="header-kicker">{{ APP_BRAND.MODULE_ENTRY_NAME }}</p> -->
            <h2 id="agent-workbench-title">{{ activeTaskTitle }}</h2>
          </div>
          <button type="button" class="drawer-close" :title="`关闭 ${APP_BRAND.MODULE_ENTRY_NAME}`" @click="closeDrawer">
            <el-icon>
              <Close />
            </el-icon>
          </button>
        </header>

        <div class="workbench-body">
          <section v-if="taskItems.length > 1" class="workbench-section task-switcher-section">
            <div class="section-header">
              <span>最近任务</span>
              <span class="section-count">{{ taskItems.length }}</span>
            </div>
            <div class="task-switcher-list">
              <button
                v-for="task in taskItems"
                :key="task.id"
                type="button"
                class="task-switcher-item"
                :class="{ active: task.isActive }"
                @click="setActiveTask(task.id)"
              >
                <span class="task-switcher-main">
                  <span class="task-switcher-title">{{ task.title }}</span>
                  <span class="task-switcher-goal">{{ task.goal }}</span>
                </span>
                <span class="task-switcher-side">
                  <span class="status-badge" :class="task.statusClass">{{ task.statusText }}</span>
                  <span class="task-switcher-time">{{ task.updatedText }}</span>
                </span>
              </button>
            </div>
          </section>
          <section class="task-panel">
            <div>
              <div class="panel-label">当前任务</div>
              <p class="task-goal">{{ activeTaskGoal }}</p>
            </div>
            <span class="status-badge" :class="taskStatusClass">
              {{ activeTaskStatusText }}
            </span>
          </section>

          <div v-if="taskLoading || traceLoading" class="workbench-state">正在加载执行数据...</div>
          <div v-else-if="taskError || traceError" class="workbench-state">
            {{ taskError || traceError }}
          </div>

          <!-- 任务步骤 -->
          <section class="workbench-section">
            <div class="section-header">
              <span>任务步骤</span>
              <span class="section-count">{{ planItems.length }}</span>
            </div>

            <ol v-if="planItems.length > 0" class="plan-list">
              <li v-for="item in planItems" :key="item.id" class="plan-item">
                <span class="timeline-dot" :class="item.statusClass"></span>
                <div class="plan-content">
                  <div class="plan-title-row">
                    <span class="plan-title">{{ item.title }}</span>
                    <span class="plan-duration">{{ item.durationText }}</span>
                  </div>
                  <p v-if="item.description" class="plan-description">{{ item.description }}</p>
                  <div class="item-meta">
                    <span>{{ item.statusText }}</span>
                    <span v-if="item.resultSummary">{{ item.resultSummary }}</span>
                    <span v-if="item.error" class="danger-text">{{ item.error }}</span>
                  </div>
                </div>
              </li>
            </ol>
            <el-empty v-else description="暂无计划步骤" :image-size="72" />
          </section>

          <!-- 工具调用时间线 -->
          <section class="workbench-section">
            <div class="section-header">
              <span>工具调用时间线</span>
              <span class="section-count">{{ logs.length }}</span>
            </div>

            <div v-if="logs.length === 0" class="workbench-state">
              本轮回复没有触发工具调用。
            </div>
            <ol v-else class="tool-timeline">
              <li v-for="log in logs" :key="log.id" class="tool-item">
                <div class="tool-header">
                  <span class="tool-name">{{ log.toolName }}</span>
                  <span class="status-badge" :class="log.status">
                    {{ getToolStatusText(log.status) }}
                  </span>
                </div>
                <div class="item-meta">
                  <span>{{ getRiskText(log.riskLevel) }}</span>
                  <span v-if="log.permissionAction">{{ getPermissionText(log.permissionAction) }}</span>
                  <span>{{ formatDuration(log.durationMs) }}</span>
                  <span>{{ formatDate(log.createdAt) }}</span>
                </div>
                <div class="summary-grid">
                  <div v-if="getPreviewSummary(log.input)" class="summary-box">
                    <span>输入摘要</span>
                    <p>{{ getPreviewSummary(log.input) }}</p>
                  </div>
                  <div v-if="getPreviewSummary(log.outputPreview)" class="summary-box">
                    <span>输出摘要</span>
                    <p>{{ getPreviewSummary(log.outputPreview) }}</p>
                  </div>
                </div>
                <p v-if="log.error" class="tool-error">{{ log.error }}</p>
              </li>
            </ol>
          </section>

          <!-- 文件变更预览 -->
          <section class="workbench-section">
            <div class="section-header">
              <span>文件变更预览</span>
              <span class="section-count">{{ fileChanges.length }}</span>
            </div>
            <div v-if="fileChanges.length === 0" class="workbench-state">
              暂未发现文件写入、删除或管理类操作。
            </div>
            <ul v-else class="file-list">
              <li v-for="file in fileChanges" :key="file.id" class="file-item">
                <span class="file-action">{{ file.action }}</span>
                <span class="file-path">{{ file.path }}</span>
                <span class="file-source">{{ file.toolName }}</span>
              </li>
            </ul>
          </section>

          <!-- 日志面板 -->
          <section class="workbench-section">
            <div class="section-header">
              <span>日志面板</span>
              <span class="section-count">{{ eventLogs.length }}</span>
            </div>
            <ol v-if="eventLogs.length > 0" class="event-list">
              <li v-for="event in eventLogs" :key="event.id" class="event-item">
                <span>{{ event.time }}</span>
                <p>{{ event.text }}</p>
              </li>
            </ol>
            <div v-else class="workbench-state">暂无执行日志。</div>
          </section>

          <!-- 人工确认 -->
          <section v-if="confirmationItems.length > 0" class="workbench-section confirmation-section">
            <div class="section-header">
              <span>人工确认</span>
              <span class="section-count">{{ confirmationItems.length }}</span>
            </div>
            <div v-for="item in confirmationItems" :key="item.id" class="confirm-card">
              <div>
                <strong>{{ item.title }}</strong>
                <p>{{ item.description }}</p>
              </div>
              <button type="button" class="confirm-button" @click="confirmTask(item.taskId)">
                确认继续
              </button>
            </div>
          </section>

          <!-- 结果交付区 -->
          <section class="workbench-section delivery-section">
            <div class="section-header">
              <span>结果交付区</span>
              <span class="section-count">{{ deliveryItems.length }}</span>
            </div>
            <ul v-if="deliveryItems.length > 0" class="delivery-list">
              <li v-for="item in deliveryItems" :key="item.id">
                <span>{{ item.label }}</span>
                <p>{{ item.value }}</p>
              </li>
            </ul>
            <div v-else class="workbench-state">任务完成后会在这里汇总报告、文件、链接或 PR 信息。</div>
          </section>
        </div>
      </aside>
    </Teleport>
  </section>
</template>

<script setup lang="ts">
import { computed, toRef } from "vue";
import { ArrowRight, Close, Monitor } from "@element-plus/icons-vue";
import {
  AgentTaskStatus,
  APP_BRAND,
  ToolExecutionStatus,
  ToolPermissionAction,
} from "~/enums";
import { useAgentWorkbench } from "~/composables/useAgentWorkbench";
import type { ThinkingStep } from "~/composables/useThinkingProcess";
import type { PlanItem } from "~/types/agent-workbench";
import {
  formatDate,
  formatDuration,
  getFilePathFromLog,
  getGoalTypeText,
  getPermissionText,
  getPreviewSummary,
  getRiskText,
  getStepDurationText,
  getStepStatusText,
  getTaskStatusText,
  getThinkingStatusText,
  getToolStatusText,
  isFileChangeLog,
  normalizeThinkingStatusClass,
} from "~/utils/agent-workbench";

const props = defineProps<{
  traceId?: string; // 追踪 ID，用于加载特定任务的执行日志。
  sessionId?: string; // 会话 ID，用于加载当前会话的会话任务。
  userId?: string; // 用户 ID，用于加载特定用户的会话任务。
  thinkingSteps?: ThinkingStep[]; // 思考步骤，用于展示当前任务的执行进度。
}>();

const normalizedSteps = computed(() => props.thinkingSteps || []);

const {
  drawerVisible,
  traceLoading,
  traceError,
  logs,
  taskLoading,
  taskError,
  tasks,
  activeTask,
  loadAgentTasks,
  setActiveTask,
  openDrawer,
  closeDrawer,
  confirmTask,
} = useAgentWorkbench({
  traceId: toRef(props, "traceId"),
  sessionId: toRef(props, "sessionId"),
  userId: toRef(props, "userId"),
});

// 当前任务信息
const activeTaskTitle = computed(() => {
  if (activeTask.value) {
    return getGoalTypeText(activeTask.value.goalType);
  }

  return "本轮回复";
});

// 当前任务目标
const activeTaskGoal = computed(() => {
  if (activeTask.value?.goal) {
    return activeTask.value.goal;
  }

  if (normalizedSteps.value.length > 0) {
    return normalizedSteps.value[0].text;
  }

  return "查看本轮 Agent 执行过程与交付信息。";
});

// 当前任务状态
const activeTaskStatusText = computed(() => {
  if (activeTask.value) {
    return getTaskStatusText(activeTask.value.status);
  }

  return logs.value.length > 0 || normalizedSteps.value.length > 0 ? "已完成" : "待查看";
});

// 当前任务状态样式
const taskStatusClass = computed(() => activeTask.value?.status || "completed");

/**
 * 当前任务的执行计划步骤信息
 */
const taskItems = computed(() =>
  tasks.value.map((task) => ({
    id: task.id,
    title: getGoalTypeText(task.goalType),
    goal: task.goal,
    statusText: getTaskStatusText(task.status),
    statusClass: task.status,
    updatedText: formatDate(task.updatedAt),
    isActive: task.id === activeTask.value?.id,
  })),
);

const planItems = computed<PlanItem[]>(() => {
  const taskSteps = activeTask.value?.steps || [];
  if (taskSteps.length > 0) {
    return taskSteps.map((step) => ({
      id: step.id,
      title: step.title,
      description: step.description,
      statusText: getStepStatusText(step.status),
      statusClass: step.status,
      durationText: getStepDurationText(step),
      resultSummary: step.resultSummary,
      error: step.error,
    }));
  }

  return normalizedSteps.value.map((step, index) => ({
    id: step.id || `thinking-${index}`,
    title: step.text,
    statusText: getThinkingStatusText(step.status),
    statusClass: normalizeThinkingStatusClass(step.status),
    durationText: formatDate(step.timestamp),
  }));
});

/**
 * 附件变更记录
 */
const fileChanges = computed(() => {
  return logs.value
    .filter((log) => isFileChangeLog(log))
    .map((log) => ({
      id: log.id,
      action: getPermissionText(log.permissionAction as ToolPermissionAction),
      path: getFilePathFromLog(log),
      toolName: log.toolName,
    }));
});

/**
 * 日志事件
 */
const eventLogs = computed(() => {
  const taskEvents = planItems.value.map((item) => ({
    id: `plan-${item.id}`,
    time: item.durationText || "-",
    text: `${item.title}：${item.statusText}`,
  }));
  const toolEvents = logs.value.map((log) => ({
    id: `tool-${log.id}`,
    time: formatDate(log.createdAt),
    text: `${log.toolName} ${getToolStatusText(log.status)}，耗时 ${formatDuration(log.durationMs)}`,
  }));

  return [...taskEvents, ...toolEvents].slice(0, 12);
});

/**
 * 人工确认步骤
 */
const confirmationItems = computed(() => {
  const task = activeTask.value;
  if (!task?.requiresConfirmation) {
    return [];
  }

  return [{
    id: task.id,
    taskId: task.id,
    title: "高风险步骤需要确认",
    description: "继续前请确认该任务允许执行写入、删除、部署或权限相关操作。",
  }];
});

const hasPendingConfirmation = computed(() => confirmationItems.value.length > 0);

onMounted(() => {
  loadAgentTasks();
});

/**
 * 交付信息
 */
const deliveryItems = computed(() => {
  const completedSteps = planItems.value.filter((item) => item.resultSummary);
  const successfulTools = logs.value.filter((log) => log.status === ToolExecutionStatus.SUCCESS);
  const items = completedSteps.map((item) => ({
    id: `step-result-${item.id}`,
    label: item.title,
    value: item.resultSummary as string,
  }));

  if (activeTask.value?.status === AgentTaskStatus.COMPLETED) {
    items.unshift({
      id: `task-${activeTask.value.id}`,
      label: "最终报告",
      value: "任务已完成，计划步骤和工具调用记录已归档。",
    });
  }

  if (successfulTools.length > 0) {
    items.push({
      id: "tool-summary",
      label: "工具产物",
      value: `本轮成功调用 ${successfulTools.length} 次工具，可在时间线查看输入输出摘要。`,
    });
  }

  return items;
});

/**
 * 触发按钮文案
 */
const triggerText = computed(() => {
  if (hasPendingConfirmation.value) {
    return `${APP_BRAND.MODULE_ENTRY_NAME} · 需要确认`;
  }

  const stepCount = planItems.value.length;
  const toolCount = logs.value.length;

  if (stepCount === 0 && toolCount === 0) {
    return `打开 ${APP_BRAND.MODULE_ENTRY_NAME}`;
  }

  return `${APP_BRAND.MODULE_ENTRY_NAME} · ${stepCount} 步 · ${toolCount} 次工具`;
});
</script>

<style scoped lang="scss">
@use './AgentTracePanel.scss';
</style>
